import React, { useState, useEffect, useCallback } from "react";
import Web3Service, {
  UserDetails,
  WrongNetworkError,
  BSC_CHAIN_ID,
  MAX_BALANCE,
  hasPositiveBalance,
} from "./services/Web3Services";
import HomePage from "./components/HomePage";
import VestingDashboard from "./components/ClaimPage";
import AdminPage from "./components/AdminPage";
import NoticeBanner from "./components/NoticeBanner";

const App: React.FC = () => {
  // Core state
  const [page, setPage] = useState<"home" | "dashboard" | "admin">("home");
  const [account, setAccount] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  // Loading and status state
  const [isLoading, setIsLoading] = useState(true);
  const [isActionInProgress, setIsActionInProgress] = useState(false); // A single state for any transaction
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // User-specific data state
  const [userState, setUserState] = useState<UserDetails | null>(null);
  // null means "could not read" — distinct from "0", a genuinely empty wallet
  const [cvt1Balance, setCvt1Balance] = useState<string | null>("0");
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);
  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);

  // NOTE: This is a placeholder for your actual admin check logic
  const checkAdminStatus = async (walletAddress: string): Promise<boolean> => {
    const admin = await Web3Service.isAdmin(walletAddress);
    return admin;
  };

  const fetchUserData = useCallback(async (walletAddress: string) => {
    if (!walletAddress) return;

    try {
      // Add a small delay for mobile wallets to stabilize
      if (window.ethereum?.isTrust || window.ethereum?.isMetaMask) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Balance: the service already retries internally and returns null on
      // failure, so "0" here always means a genuinely empty wallet.
      const cvt1Bal = await Web3Service.getBalance(
        walletAddress,
        process.env.REACT_APP_CVT1_TOKEN_ADDRESS || ""
      );
      setCvt1Balance(cvt1Bal);
      setIsWrongNetwork(false);

      let details: UserDetails;

      try {
        details = await Web3Service.getUserDetails(walletAddress);
      } catch (error) {
        console.warn("Could not retrieve vesting details, assuming new user.");
        details = {
          vestedToken: "0",
          startTime: 0,
          tokenReleased: "0",
          claimableAmount: "0",
          feesDeposited: false,
          claimTriggered: false,
          onHold: false,
          adminApproved: false,
          vestingStarted: false,
          firstClaimDone: false,
          feesTobePaid: "0",
        };
      }

      setUserState(details);

      // Routing Logic
      if (details && hasPositiveBalance(details.vestedToken)) {
        setPage("dashboard");
      } else {
        setPage("home");
      }
    } catch (error) {
      if (error instanceof WrongNetworkError) {
        console.warn("Wallet on unsupported chain:", error.currentChainId);
        setIsWrongNetwork(true);
        setCvt1Balance(null);
        return;
      }
      console.error("Failed to fetch critical user data:", error);
      setCvt1Balance(null);
      setStatusMessage({
        type: "error",
        text: "Could not connect to the service. Please refresh.",
      });
    }
  }, []);

  const handleSwitchNetwork = useCallback(async () => {
    setIsSwitchingNetwork(true);
    try {
      const switched = await Web3Service.switchToBSC();
      if (switched) {
        setIsWrongNetwork(false);
        if (account) await fetchUserData(account);
      } else {
        setStatusMessage({
          type: "error",
          text: "Network switch was cancelled. Please select BNB Smart Chain in your wallet.",
        });
      }
    } finally {
      setIsSwitchingNetwork(false);
    }
  }, [account, fetchUserData]);

  const handleRetryBalance = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    try {
      await fetchUserData(account);
    } finally {
      setIsLoading(false);
    }
  }, [account, fetchUserData]);

  useEffect(() => {
    const autoConnect = async () => {
      setIsLoading(true);
      try {
        // Check if we're in a mobile wallet environment
        const isMobileWallet =
          window.ethereum?.isTrust ||
          window.ethereum?.isTokenPocket ||
          window.ethereum?.isCoinbaseWallet ||
          (window.ethereum?.isMetaMask &&
            /Mobile|Android|iPhone/i.test(navigator.userAgent));

        if (
          Web3Service.isMetaMaskAvailable() &&
          window.ethereum.selectedAddress
        ) {
          // Add delay for mobile wallets
          if (isMobileWallet) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          const walletAddress = await Web3Service.connectWallet();
          setAccount(walletAddress);
          await fetchUserData(walletAddress);
        }
      } catch (error) {
        if (error instanceof WrongNetworkError) {
          console.warn("Wallet on unsupported chain:", error.currentChainId);
          setIsWrongNetwork(true);
          setCvt1Balance(null);
        } else {
          console.error("Auto-connect failed:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };
    autoConnect();
  }, [fetchUserData]);

  // React to the user switching chains in their wallet rather than requiring
  // a manual refresh.
  useEffect(() => {
    if (!window.ethereum?.on) return;

    const handleChainChanged = (chainId: string) => {
      const onBSC = chainId.toLowerCase() === BSC_CHAIN_ID;
      setIsWrongNetwork(!onBSC);
      if (onBSC && account) {
        setIsLoading(true);
        fetchUserData(account).finally(() => setIsLoading(false));
      } else if (!onBSC) {
        setCvt1Balance(null);
      }
    };

    window.ethereum.on("chainChanged", handleChainChanged);
    return () => window.ethereum.removeListener?.("chainChanged", handleChainChanged);
  }, [account, fetchUserData]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (account && page === "dashboard" && !isAdmin) {
      intervalId = setInterval(() => {
        fetchUserData(account);
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(intervalId);
  }, [account, page, isAdmin, fetchUserData]);

  // Handler for the main "Connect Wallet" button
  const handleConnectWallet = async () => {
    setIsLoading(true);
    try {
      const walletAddress = await Web3Service.connectWallet();
      setAccount(walletAddress);

      if (await checkAdminStatus(walletAddress)) {
        setIsAdmin(true);
        setPage("admin");
      } else {
        setIsAdmin(false);
        await fetchUserData(walletAddress);
      }
    } catch (error: any) {
      if (error instanceof WrongNetworkError) {
        console.warn("Wallet on unsupported chain:", error.currentChainId);
        setIsWrongNetwork(true);
        setCvt1Balance(null);
        return;
      }
      console.error("Failed to connect wallet:", error);
      setStatusMessage({
        type: "error",
        text: error.message || "Failed to connect wallet.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  // Update the handleInitialDeposit function
  const handleInitialDeposit = async () => {
    if (!cvt1Balance || !hasPositiveBalance(cvt1Balance)) return;
    setIsActionInProgress(true);
    setStatusMessage(null);
    try {
      // MAX_BALANCE deposits the on-chain figure directly. Converting to a
      // decimal string and back was the source of the rounding mismatch.
      await Web3Service.depositCVT(MAX_BALANCE);
      setStatusMessage({
        type: "success",
        text: "Deposit successful! Welcome to the vesting program.",
      });
      // After a successful deposit, fetch all data to transition to the dashboard
      await fetchUserData(account);
    } catch (error: any) {
      console.error("Initial deposit failed", error);
      setStatusMessage({
        type: "error",
        text: error.message || "Initial deposit failed.",
      });
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Handler for paying the vesting fee
  const handleDepositFee = async (): Promise<boolean> => {
    setIsActionInProgress(true);
    setStatusMessage(null);
    try {
      await Web3Service.payFee();
      setStatusMessage({
        type: "success",
        text: "Fee deposited successfully! Your vesting schedule is now active.",
      });
      await fetchUserData(account); // Refresh data
      return true;
    } catch (error: any) {
      console.error("Fee deposit failed:", error);
      setStatusMessage({
        type: "error",
        text: error.message || "Fee deposit failed.",
      });
      return false;
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Modify handleClaimTokens to show specific errors
  const handleClaimTokens = async () => {
    setIsActionInProgress(true);
    setStatusMessage(null);

    try {
      const result = await Web3Service.claimTokens();
      if (result.success) {
        setStatusMessage({
          type: "success",
          text: "Tokens claimed successfully!",
        });
        await fetchUserData(account);
      }
    } catch (error: any) {
      console.error("Claim failed:", error);
      setStatusMessage({
        type: "error",
        text: error.message || "Claim failed. Please try again.",
      });
    } finally {
      setIsActionInProgress(false);
    }
  };
  const renderPage = () => {
    if (isLoading) {
      return (
        <div
          className="min-h-screen w-full flex flex-col items-center justify-center gap-4 text-white font-sora"
          style={{
            background: `radial-gradient(1200px 700px at top left, rgb(2, 36, 19), rgb(0, 2, 5) 30%)`,
          }}
        >
          <div className="w-10 h-10 rounded-full border-2 border-[#00FF7F]/25 border-t-[#00FF7F] animate-spin" />
          <p className="text-gray-400 text-sm font-semibold">Connecting wallet...</p>
        </div>
      );
    }
    // Wrong chain blocks everything — contract reads are meaningless off BSC.
    if (isWrongNetwork) {
      return (
        <div
          className="min-h-screen w-full flex flex-col items-center justify-center px-6 text-white font-sora"
          style={{
            background: `radial-gradient(1200px 700px at top left, rgb(2, 36, 19), rgb(0, 2, 5) 30%)`,
          }}
        >
          <div className="w-full max-w-sm bg-[#1A1A1A] rounded-2xl p-6 shadow-lg border border-[#333] text-center">
            <div className="w-12 h-12 rounded-full bg-yellow-500/15 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-yellow-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-bold mb-2">Wrong Network</h2>
            <p className="text-gray-400 text-sm mb-6">
              This app runs on the BNB Smart Chain. Please switch your wallet to
              BSC to see your CVT balance.
            </p>
            <button
              onClick={handleSwitchNetwork}
              disabled={isSwitchingNetwork}
              className="w-full bg-gradient-to-r from-[#00FF7F] to-[#00D06C] text-black font-bold py-3 px-6 rounded-xl shadow-md disabled:opacity-60 flex items-center justify-center transition-opacity duration-300"
            >
              {isSwitchingNetwork ? (
                <>
                  <span className="w-4 h-4 mr-2 rounded-full border-2 border-black/25 border-t-black animate-spin" />
                  Switching...
                </>
              ) : (
                "Switch to BNB Smart Chain"
              )}
            </button>
          </div>
        </div>
      );
    }

    if (isAdmin) {
      return <AdminPage account={account} />;
    }

    switch (page) {
      case "dashboard":
        return (
          <VestingDashboard
            account={account}
            userState={userState}
            onDepositFee={handleDepositFee}
            onClaimTokens={handleClaimTokens}
            isActionInProgress={isActionInProgress}
            statusMessage={statusMessage}
          />
        );
      case "home":
      default:
        return (
          <HomePage
            onConnectWallet={handleConnectWallet}
            isConnecting={isLoading}
            account={account}
            cvt1Balance={cvt1Balance}
            onRetryBalance={handleRetryBalance}
            onDeposit={handleInitialDeposit}
            isDepositing={isActionInProgress}
            statusMessage={statusMessage}
          />
        );
    }
  };

  return (
    <div className="App">
      {/* Site-wide notice. Renders nothing unless one is active and unexpired. */}
      <NoticeBanner />
      {renderPage()}
    </div>
  );
};

export default App;
